import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Icon } from '@iconify/react'
import { CpfCnpjInput } from '@/components/CpfCnpjInput'
import {
  formatDate,
  formatBirthDateInput,
  formatPhone,
  formatPostalCode,
  type PasswordFormState,
  type ProfileFormState,
} from './landing-profile-format'
import styles from './LandingNumbersPage.module.css'

const BRAZILIAN_STATES =
  'AC AL AP AM BA CE DF ES GO MA MT MS MG PA PB PR PE PI RJ RN RS RO RR SC SP SE TO'.split(
    ' '
  )

type LandingProfileCadastroProps = {
  profileForm: ProfileFormState
  isSavingProfile: boolean
  profileError: string | null
  profileSuccess: string | null
  onProfileFieldChange: <K extends keyof ProfileFormState>(
    field: K,
    value: ProfileFormState[K]
  ) => void
  onSaveProfile: () => void
  saveProfileButtonClassName: string
}

type LandingProfileSecurityProps = {
  profileForm: ProfileFormState
  passwordForm: PasswordFormState
  isSavingPassword: boolean
  securityError: string | null
  securitySuccess: string | null
  institutionName: string
  isGuest: boolean
  onPasswordFieldChange: (field: keyof PasswordFormState, value: string) => void
  onChangePassword: () => void
  savePasswordButtonClassName: string
}

function ProfileField({
  id,
  label,
  icon,
  wide,
  children,
}: {
  id: string
  label: string
  icon: string
  wide?: boolean
  children: ReactNode
}) {
  return (
    <div className={wide ? `${styles.field} ${styles.fieldWide}` : styles.field}>
      <label className={styles.label} htmlFor={id}>
        {label}
      </label>
      <div className={styles.control}>
        <Icon icon={icon} className={styles.controlIcon} aria-hidden />
        {children}
      </div>
    </div>
  )
}

export function LandingProfileCadastro({
  profileForm,
  isSavingProfile,
  profileError,
  profileSuccess,
  onProfileFieldChange,
  onSaveProfile,
  saveProfileButtonClassName,
}: LandingProfileCadastroProps) {
  const documentLabel = profileForm.person_type === 'pj' ? 'CNPJ' : 'CPF'
  const numberInputRef = useRef<HTMLInputElement>(null)
  const [cepStatus, setCepStatus] = useState('')
  const [lookupCep, setLookupCep] = useState('')

  useEffect(() => {
    if (lookupCep.length !== 8) return
    const controller = new AbortController()
    const timeout = window.setTimeout(async () => {
      setCepStatus('Buscando endereço...')
      try {
        const response = await fetch(`https://viacep.com.br/ws/${lookupCep}/json/`, {
          signal: controller.signal,
        })
        if (!response.ok) throw new Error('Consulta indisponível')
        const data = (await response.json()) as Record<string, unknown>
        if (controller.signal.aborted) return
        if (data.erro === true) {
          setCepStatus('CEP não encontrado. Preencha o endereço manualmente.')
          return
        }
        const fields = {
          street: data.logradouro,
          neighborhood: data.bairro,
          city: data.localidade,
          state: data.uf,
        } as const
        for (const [field, value] of Object.entries(fields)) {
          if (
            typeof value === 'string' &&
            value.trim() &&
            (field !== 'state' || BRAZILIAN_STATES.includes(value))
          ) {
            onProfileFieldChange(
              field as keyof typeof fields,
              value.trim().slice(0, field === 'state' ? 2 : 255)
            )
          }
        }
        setCepStatus('Endereço encontrado. Confira os dados e informe o número.')
        numberInputRef.current?.focus()
      } catch {
        if (!controller.signal.aborted)
          setCepStatus('Não foi possível buscar o CEP. Preencha o endereço manualmente.')
      }
    }, 250)
    return () => {
      window.clearTimeout(timeout)
      controller.abort()
    }
  }, [lookupCep, onProfileFieldChange])

  return (
    <section className={styles.stack} aria-labelledby="landing-profile-data-title">
      <div>
        <h2 id="landing-profile-data-title" className={styles.sectionTitle}>
          Dados do cadastro
        </h2>
        <p className={styles.sectionText}>
          Complete seus dados para aproveitar todos os benefícios da campanha.
        </p>
      </div>

      <div className={styles.formGrid}>
        <ProfileField
          id="profile-person-type"
          label="Tipo de pessoa"
          icon="mdi:account-outline"
        >
          <select
            id="profile-person-type"
            className={styles.select}
            value={profileForm.person_type}
            onChange={(event) =>
              onProfileFieldChange('person_type', event.target.value as 'pf' | 'pj')
            }
            disabled={isSavingProfile}
          >
            <option value="pf">Pessoa física</option>
            <option value="pj">Pessoa jurídica</option>
          </select>
        </ProfileField>

        {profileForm.person_type === 'pj' ? (
          <ProfileField
            id="profile-corporate-name"
            label="Razão social"
            icon="mdi:domain"
            wide
          >
            <input
              id="profile-corporate-name"
              className={styles.input}
              value={profileForm.corporate_name}
              onChange={(event) =>
                onProfileFieldChange('corporate_name', event.target.value)
              }
              disabled={isSavingProfile}
            />
          </ProfileField>
        ) : (
          <ProfileField
            id="profile-full-name"
            label="Nome completo"
            icon="mdi:badge-account-outline"
            wide
          >
            <input
              id="profile-full-name"
              className={styles.input}
              autoComplete="name"
              maxLength={255}
              value={profileForm.full_name}
              onChange={(event) => onProfileFieldChange('full_name', event.target.value)}
              disabled={isSavingProfile}
            />
          </ProfileField>
        )}

        <ProfileField
          id="profile-document"
          label={documentLabel}
          icon="mdi:card-account-details-outline"
        >
          <CpfCnpjInput
            id="profile-document"
            mode={profileForm.person_type === 'pj' ? 'cnpj' : 'cpf'}
            value={profileForm.document}
            onChange={(masked) => onProfileFieldChange('document', masked)}
            className={styles.input}
            disabled={isSavingProfile}
          />
        </ProfileField>

        <ProfileField id="profile-email" label="E-mail" icon="mdi:email-outline">
          <input
            id="profile-email"
            className={styles.input}
            type="email"
            inputMode="email"
            autoComplete="email"
            maxLength={255}
            value={profileForm.email}
            onChange={(event) => onProfileFieldChange('email', event.target.value)}
            disabled={isSavingProfile}
          />
        </ProfileField>

        <ProfileField id="profile-phone" label="Telefone" icon="mdi:phone-outline">
          <input
            id="profile-phone"
            className={styles.input}
            type="tel"
            inputMode="numeric"
            autoComplete="tel"
            maxLength={15}
            placeholder="(11) 99999-9999"
            value={profileForm.phone}
            onChange={(event) =>
              onProfileFieldChange('phone', formatPhone(event.target.value))
            }
            disabled={isSavingProfile}
          />
        </ProfileField>

        <ProfileField id="profile-gender" label="Gênero" icon="mdi:gender-male-female">
          <select
            id="profile-gender"
            className={styles.select}
            value={profileForm.gender}
            onChange={(event) => onProfileFieldChange('gender', event.target.value)}
            disabled={isSavingProfile}
          >
            <option value="">Selecione (opcional)</option>
            <option value="male">Masculino</option>
            <option value="female">Feminino</option>
            <option value="non_binary">Não binário</option>
            <option value="prefer_not_to_say">Prefiro não informar</option>
          </select>
        </ProfileField>

        <ProfileField
          id="profile-birth-date"
          label="Data de nascimento"
          icon="mdi:calendar-outline"
        >
          <input
            id="profile-birth-date"
            className={styles.input}
            type="text"
            inputMode="numeric"
            autoComplete="bday"
            maxLength={10}
            placeholder="DD/MM/AAAA"
            value={profileForm.birth_date}
            onChange={(event) =>
              onProfileFieldChange('birth_date', formatBirthDateInput(event.target.value))
            }
            disabled={isSavingProfile}
          />
        </ProfileField>

        <ProfileField id="profile-website" label="Website (opcional)" icon="mdi:web">
          <input
            id="profile-website"
            className={styles.input}
            type="url"
            inputMode="url"
            autoComplete="url"
            maxLength={512}
            value={profileForm.website}
            onChange={(event) => onProfileFieldChange('website', event.target.value)}
            disabled={isSavingProfile}
            placeholder="https://"
          />
        </ProfileField>
      </div>

      <h3 id="landing-profile-address-title" className={styles.subheading}>
        Endereço
      </h3>

      <div className={styles.formGrid}>
        <ProfileField
          id="profile-postal-code"
          label="CEP"
          icon="mdi:mailbox-outline"
          wide
        >
          <input
            id="profile-postal-code"
            className={styles.input}
            type="text"
            inputMode="numeric"
            autoComplete="postal-code"
            maxLength={9}
            placeholder="00000-000"
            value={profileForm.postal_code}
            onChange={(event) => {
              const digits = event.target.value.replace(/\D/g, '').slice(0, 8)
              setCepStatus('')
              setLookupCep(digits)
              onProfileFieldChange('postal_code', formatPostalCode(digits))
            }}
            disabled={isSavingProfile}
            aria-describedby="profile-cep-status"
          />
        </ProfileField>
        <p id="profile-cep-status" className={styles.cepStatus} role="status">
          {cepStatus || 'Digite o CEP para preencher o endereço automaticamente.'}
        </p>
        <ProfileField id="profile-street" label="Rua" icon="mdi:home-outline" wide>
          <input
            id="profile-street"
            className={styles.input}
            autoComplete="address-line1"
            maxLength={255}
            value={profileForm.street}
            onChange={(event) => onProfileFieldChange('street', event.target.value)}
            disabled={isSavingProfile}
          />
        </ProfileField>
        <ProfileField id="profile-number" label="Número" icon="mdi:numeric">
          <input
            id="profile-number"
            ref={numberInputRef}
            className={styles.input}
            maxLength={50}
            value={profileForm.number}
            onChange={(event) => onProfileFieldChange('number', event.target.value)}
            disabled={isSavingProfile}
          />
        </ProfileField>
        <ProfileField
          id="profile-complement"
          label="Complemento"
          icon="mdi:home-plus-outline"
        >
          <input
            id="profile-complement"
            className={styles.input}
            autoComplete="address-line2"
            maxLength={255}
            value={profileForm.complement}
            onChange={(event) => onProfileFieldChange('complement', event.target.value)}
            disabled={isSavingProfile}
          />
        </ProfileField>
        <ProfileField
          id="profile-neighborhood"
          label="Bairro"
          icon="mdi:map-marker-outline"
        >
          <input
            id="profile-neighborhood"
            className={styles.input}
            maxLength={255}
            value={profileForm.neighborhood}
            onChange={(event) => onProfileFieldChange('neighborhood', event.target.value)}
            disabled={isSavingProfile}
          />
        </ProfileField>
        <ProfileField id="profile-city" label="Cidade" icon="mdi:city-variant-outline">
          <input
            id="profile-city"
            className={styles.input}
            autoComplete="address-level2"
            maxLength={255}
            value={profileForm.city}
            onChange={(event) => onProfileFieldChange('city', event.target.value)}
            disabled={isSavingProfile}
          />
        </ProfileField>
        <ProfileField id="profile-state" label="Estado" icon="mdi:map-outline">
          <select
            id="profile-state"
            className={styles.select}
            value={profileForm.state}
            onChange={(event) => onProfileFieldChange('state', event.target.value)}
            disabled={isSavingProfile}
          >
            <option value="">Selecione</option>
            {BRAZILIAN_STATES.map((uf) => (
              <option key={uf} value={uf}>
                {uf}
              </option>
            ))}
          </select>
        </ProfileField>
      </div>

      {profileError ? <p className={styles.error}>{profileError}</p> : null}
      {profileSuccess ? <p className={styles.success}>{profileSuccess}</p> : null}

      <div className={styles.actions}>
        <button
          type="button"
          className={saveProfileButtonClassName}
          onClick={onSaveProfile}
          disabled={isSavingProfile}
        >
          {isSavingProfile ? 'Salvando...' : 'Salvar dados do perfil'}
        </button>
      </div>
    </section>
  )
}

export function LandingProfileSecurity({
  profileForm,
  passwordForm,
  isSavingPassword,
  securityError,
  securitySuccess,
  institutionName,
  isGuest,
  onPasswordFieldChange,
  onChangePassword,
  savePasswordButtonClassName,
}: LandingProfileSecurityProps) {
  return (
    <section className={styles.stack} aria-labelledby="landing-profile-password-title">
      <div>
        <h2 id="landing-profile-password-title" className={styles.sectionTitle}>
          Trocar senha
        </h2>
        <p className={styles.sectionText}>
          Atualize a senha da sua conta Sortee, válida para todas as campanhas.
        </p>
      </div>

      <div className={styles.formStack}>
        <ProfileField
          id="profile-current-password"
          label="Senha atual"
          icon="mdi:lock-outline"
        >
          <input
            id="profile-current-password"
            className={styles.input}
            type="password"
            value={passwordForm.current_password}
            onChange={(event) =>
              onPasswordFieldChange('current_password', event.target.value)
            }
            disabled={isSavingPassword}
          />
        </ProfileField>
        <ProfileField
          id="profile-new-password"
          label="Nova senha"
          icon="mdi:lock-plus-outline"
        >
          <input
            id="profile-new-password"
            className={styles.input}
            type="password"
            value={passwordForm.new_password}
            onChange={(event) =>
              onPasswordFieldChange('new_password', event.target.value)
            }
            disabled={isSavingPassword}
          />
        </ProfileField>
        <ProfileField
          id="profile-new-password-confirm"
          label="Confirmar nova senha"
          icon="mdi:lock-check-outline"
        >
          <input
            id="profile-new-password-confirm"
            className={styles.input}
            type="password"
            value={passwordForm.new_password_confirmation}
            onChange={(event) =>
              onPasswordFieldChange('new_password_confirmation', event.target.value)
            }
            disabled={isSavingPassword}
          />
        </ProfileField>
      </div>

      {securityError ? <p className={styles.error}>{securityError}</p> : null}
      {securitySuccess ? <p className={styles.success}>{securitySuccess}</p> : null}

      <div className={styles.actions}>
        <button
          type="button"
          className={savePasswordButtonClassName}
          onClick={onChangePassword}
          disabled={isSavingPassword}
        >
          {isSavingPassword ? 'Atualizando...' : 'Atualizar senha'}
        </button>
      </div>

      <h3 id="landing-profile-summary-title" className={styles.subheading}>
        Resumo do cadastro
      </h3>
      <div className={styles.infoList}>
        <div className={styles.infoRow}>
          <span>Status da conta</span>
          <strong>{isGuest ? 'Convidado' : 'Ativa'}</strong>
        </div>
        <div className={styles.infoRow}>
          <span>Documento</span>
          <strong>{profileForm.document || '-'}</strong>
        </div>
        <div className={styles.infoRow}>
          <span>Nascimento</span>
          <strong>{formatDate(profileForm.birth_date)}</strong>
        </div>
        <div className={styles.infoRow}>
          <span>Instituição da campanha atual</span>
          <strong>{institutionName}</strong>
        </div>
      </div>
    </section>
  )
}
